import {ChangeDetectionStrategy, ChangeDetectorRef, Component,} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FormFieldConfig} from "../../../../model/api/form-field-config";
import {ClearingFlexService} from "../../../../services/clearing/flex/clearing-flex.service";
import {FlexTradesRequest} from "../../../../model/clearing/flex/flex-trades-request";
import {Owner} from "../../../../model/customer/owner";
import {Trade} from "../../../../model/clearing/trade";
import {CommonFieldService} from "../../../../services/common/common-field.service";
import {DateTime} from "luxon";
import {FlexTrade} from "../../../../model/clearing/flex/flex-trade";
import {MaturityUtils} from "../../../../utils/maturity-utils";
import {AppConfigService} from "../../../../services/app-config.service";
import {ClearingTradeInfoService} from "../../../../services/clearing/clearing-trade-info.service";
import {FlexFormDialogBaseComponent} from "../flex-form-dialog-base.component";
import {Account} from "../../../../model/clearing/account";
import {DateFormatPipe} from "../../../../utils/date-format.pipe";
import {FieldsService} from "../../../../services/common/fields-service";
import {
  ClearingFlexTradeDataLoadingService
} from "../../../../services/clearing/flex/clearing-flex-trade-data-loading.service";
import TYPE = FormFieldConfig.TYPE;
import Side = Trade.Side;

@Component({
  selector: 'app-flex-add-form-dialog',
  templateUrl: './flex-add-form-dialog.component.html',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlexAddFormDialogComponent extends FlexFormDialogBaseComponent {

  formRecord: FlexTradesRequest = undefined;

  constructor(cdref: ChangeDetectorRef,
              clearingFlexService: ClearingFlexService,
              messageService: MessageService,
              configService: AppConfigService,
              clearingTradeInfoService: ClearingTradeInfoService,
              commonFieldService: CommonFieldService,
              translateService: TranslateService,
              fieldsService: FieldsService,
              private dataLoadingService: ClearingFlexTradeDataLoadingService) {
    super(cdref,
      clearingFlexService,
      messageService,
      configService,
      clearingTradeInfoService,
      commonFieldService,
      translateService,
      fieldsService);
  }

  protected initConfigs() {
    super.initConfigs();
    this.dataLoadingService.loadData(this.tracker);

    this.dialogFormTitle = this.getTranslation('page.' + this.transPartKey + '.dialog-trade-title');


    this.formConfig = [
      {
        group: 'upload',
        fields: [
          {
            field: 'mapping',
            column: 3,
            type: TYPE.DROPDOWN,
            options: record => this.dataLoadingService.tradeFlexTemplates,
            hidden: record => this.dataLoadingService.tradeFlexTemplates.length < 2
          },
          {
            column: 9,
            type: TYPE.NONE,
            hidden: record => this.dataLoadingService.tradeFlexTemplates.length < 2
          },
          {
            field: 'file',
            column: 12,
            type: TYPE.FILEUPLOAD,
            accept: '.csv,.xml,.json',
            validate: (rec: any) => this.validateUploadFile(),
          }],
      },
      {
        group: 'trades',
        fields: [
          {
            field: 'ownerCode',
            label: 'Owner',
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.dataLoadingService.owners,
            required: true,
            hidden: () => this.dataLoadingService.owners.length == 1
          },
          {
            field: 'trades',
            filter: true,
            column: 12,
            type: TYPE.ADDDELETEFIELD,
            changed: (formRecords: any) => this.refreshInfosFromTrades(formRecords),
            validate: (rec: any) => this.validateTrades(),
            richFieldConfig: {
              canDelete: formRecords => this.canDeleteFlex(formRecords),
              canAdd: formRecords => this.canAddFlex(formRecords),
              addNew: formRecords => this.addNewFlex(formRecords),
              fields: [
                {
                  group: '',
                  fields: [
                    // { field: 'clearingHouse', label: 'Clearing House', column: 2, type: TYPE.DROPDOWN, options: () => this.clearingHouse, required:true },
                    {
                      field: 'exchangeCode',
                      icon: (id: string) => {
                        if (this.dataLoadingService.exchanges) {
                          const exchField = this.dataLoadingService.exchanges.find(exchange => exchange.id === id);
                          if (exchField && exchField.value)
                            return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
                        }
                        return 'fas fa-globe-europe';
                      },
                      filter: true,
                      column: 2,
                      type: TYPE.DROPDOWN,
                      options: () => this.dataLoadingService.exchanges,
                    },
                    {field: 'displayReference', column: 2, type: TYPE.TEXT},
                    {field: 'businessDate', column: 2, type: TYPE.DATE},
                    {field: 'contractCode', column: 2, type: TYPE.TEXT},
                    {
                      field: 'side',
                      column: 2,
                      type: TYPE.DROPDOWN,
                      options: () => this.dataLoadingService.sideOptions,
                    },
                    {
                      field: 'type',
                      column: 2,
                      type: TYPE.DROPDOWN,
                      options: () => this.dataLoadingService.tradeTypes,
                    },
                    {
                      field: 'maturity',
                      column: 2,
                      type: TYPE.TEXT,
                      validate: (fTrade: FlexTrade) => !MaturityUtils.isMaturityValid(fTrade.maturity) ? 'Maturity is invalid (ddMMMyyyy,\'W\'WMMMyyyy,MMMyyyy)' : '',
                    },
                    {
                      field: 'volume', column: 2, type: TYPE.INTEGER,
                    },
                    {
                      field: 'price', column: 2, type: TYPE.DECIMAL,
                    },
                    {field: 'customOrderReference', column: 2, type: TYPE.TEXT},
                  ]
                },
                {
                  group: 'details',
                  collapsed: true,
                  fields: [
                    {field: 'deleted', column: 2, type: TYPE.CHECKBOX},
                    // {field: 'account.name', column: 2, type: TYPE.TEXT},
                    // {field: 'account.type', column: 2, type: TYPE.TEXT},
                    {field: 'account.reference1', column: 2, type: TYPE.TEXT},
                    // {field: 'account.reference2', column: 2, type: TYPE.TEXT},
                    // {field: 'account.reference3', column: 2, type: TYPE.TEXT},
                    // {field: 'avgPrice', column: 2, type: TYPE.DECIMAL},
                    // {field: 'avgPriceGroupId', column: 2, type: TYPE.TEXT},
                    // {field: 'avgPriceResidual', column: 2, type: TYPE.DECIMAL},
                    // {field: 'avgPriceResidualCurrency', column: 2, type: TYPE.TEXT},
                    // {field: 'avgRndPrice', column: 2, type: TYPE.DECIMAL},
                    {field: 'callPut', column: 2, type: TYPE.DROPDOWN, options: () => this.dataLoadingService.callPuts},
                    {field: 'clearingDate', column: 2, type: TYPE.DATE},
                    {field: 'contractUnit', column: 2, type: TYPE.TEXT},
                    {field: 'exchangeReference', column: 2, type: TYPE.TEXT},
                    {field: 'executionDate', column: 2, type: TYPE.DATETIME},
                    {
                      field: 'openClose',
                      column: 2,
                      type: TYPE.DROPDOWN,
                      options: () => this.dataLoadingService.openCloses
                    },
                    {field: 'orderId', column: 2, type: TYPE.TEXT},
                    {field: 'orderReference', column: 2, type: TYPE.TEXT},
                    {field: 'origineTrader', column: 2, type: TYPE.TEXT},
                    {field: 'strike', column: 2, type: TYPE.DECIMAL},
                    {field: 'trader', column: 2, type: TYPE.TEXT},
                    {field: 'tradingEnvironment', column: 2, type: TYPE.TEXT},
                    // {field: 'giveInMessage', column: 2, type: TYPE.TEXT},
                    // {field: 'giveInMessage2', column: 2, type: TYPE.TEXT},
                    // {field: 'giveInMessage3', column: 2, type: TYPE.TEXT},
                    {field: 'giveUpBrokerCode', column: 2, type: TYPE.TEXT},
                    // {field: 'giveUpCommission', column: 2, type: TYPE.TEXT},
                    // {field: 'giveUpMessage', column: 2, type: TYPE.TEXT},
                    // {field: 'giveupAccountType', column: 2, type: TYPE.TEXT},
                    // {field: 'giveupGroupId', column: 2, type: TYPE.TEXT},
                    // {field: 'giveupMessage2', column: 2, type: TYPE.TEXT},
                    // {field: 'giveupMessage3', column: 2, type: TYPE.TEXT}
                  ]
                },
              ]
            }
          }]
      }];

  }

  private validateUploadFile() {
    return this.formRecord && (this.formRecord.file || this.formRecord.trades && this.formRecord.trades.length > 0) ? '' : 'No file selected';
  }

  public validateTrades() {
    if (this.formRecord.trades && this.formRecord.trades.length > 0) {
      for (let trade of this.formRecord.trades) {
        if (!trade.exchangeCode || trade.exchangeCode.length == 0
          || !trade.contractCode || trade.contractCode.length == 0
          || !trade.displayReference || trade.displayReference.length == 0
          || !trade.businessDate
          || !trade.type || trade.type.length == 0
          || !trade.side || trade.side.length == 0
          || !trade.volume || !trade.price) {
          return 'Please fill in all mandatory fields';
        }
      }
    }
    return ''
  }

  canDeleteFlex(formRecords: any): boolean {
    return true; //this.formRecord['trades'].length > 1;
  }

  canAddFlex(formRecords: any): boolean {
    return true;
    // let splitTotVolume: number = 0;
    // this.formRecord['trades'].forEach(formRecord => {
    //   splitTotVolume += parseInt(formRecord.allocation);
    // });
    // return splitTotVolume < this.formRecord.volume;
  }

  addNewFlex(formRecords: any) {
    if (!this.formRecord.trades || this.formRecord.trades.length == 0) {
      this.formRecord.trades.push({
        account: {
          name: '',
          type: '',
          reference1: '',
          reference2: '',
          reference3: ''
        },
        agressorInd: undefined,
        alertText: "",
        audit1: "",
        audit2: "",
        audit3: "",
        audit4: "",
        audit5: "",
        avgPrice: 0,
        avgPriceGroupId: "",
        avgPriceResidual: 0,
        avgPriceResidualAlloc: 0,
        avgPriceResidualCurrency: "",
        avgPriceResidualGroup: 0,
        avgRndPrice: 0,
        businessDate: new Date(),
        callPut: undefined,
        clearingBroker: "",
        clearingDate: new Date(),
        clearingHouse: "",
        clearingInfo: undefined,
        clearings: [],
        complexStrategy: "",
        contractCode: "",
        contractUnit: "",
        contractVersion: "",
        counterPartyMember: "",
        counterPartyTrader: "",
        creationDate: undefined,
        customFreeTextField1: "",
        customFreeTextField2: "",
        customFreeTextField3: "",
        customFreeTextField4: "",
        customOrderReference: "",
        deleted: false,
        deltas: "",
        displayReference: "",
        exchangeCode: "",
        exchangeMatched: false,
        exchangeReference: "",
        exchangeStrategy: "",
        execId: "",
        executingFirm: "",
        executionDate: undefined,
        finalPrice: false,
        freeText1: "",
        freeText2: "",
        freeText3: "",
        freeText4: "",
        giveInBroker: "",
        giveInCommission: "",
        giveInMessage: "",
        giveInMessage2: "",
        giveInMessage3: "",
        giveUpBrokerCode: "",
        giveUpCommission: "",
        giveUpMessage: "",
        giveupAccountType: "",
        giveupGroupId: "",
        giveupMessage2: "",
        giveupMessage3: "",
        id: "",
        initialAccountRef: "",
        initialAccountRef2: "",
        initialAccountRef3: "",
        initialAccountType: "",
        initialOpenClose: undefined,
        giveIn: false,
        giveUp: false,
        localExecutionTime: "",
        maturity: "",
        modificationDate: undefined,
        obLegRank: 0,
        openClose: undefined,
        operatorId: "",
        orderId: "",
        orderReference: "",
        origineTrader: "",
        overrideAvgPrice: 0,
        owner: this.dataLoadingService.owners.length > 0 ? this.dataLoadingService.owners[0].text : "",
        ownerCode: this.dataLoadingService.owners.length > 0 ? this.dataLoadingService.owners[0].id : "",
        parentTradeId: "",
        price: 0,
        reversalRejected: false,
        reversalRequested: false,
        side: Side.BUY,
        source: "WEB_FLEX",
        state: undefined,
        strategy: "",
        strategyMissing: "",
        strike: 0,
        technicalFreeText1: "",
        technicalFreeText2: "",
        technicalFreeText3: "",
        technicalFreeText4: "",
        trader: "",
        tradingEnvironment: "",
        type: this.dataLoadingService.tradeTypes[0].id,
        unmatchedVolume: 0,
        volume: 0
      });
    } else {
      const lastOriginTrade = this.formRecord.trades[this.formRecord.trades.length - 1];
      const lastTrade: FlexTrade = JSON.parse(JSON.stringify(lastOriginTrade));
      lastTrade.displayReference = (isNaN(parseInt(lastTrade.displayReference)) ?
        this.formRecord.trades.length : parseInt(lastTrade.displayReference) + 1).toString();
      lastTrade.businessDate = lastOriginTrade.businessDate;
      lastTrade.clearingDate = lastOriginTrade.clearingDate;
      lastTrade.executionDate = lastOriginTrade.executionDate;
      lastTrade.creationDate = DateTime.now();
      lastTrade.modificationDate = DateTime.now();
      this.formRecord.trades.push(lastTrade);
    }
    this.refreshInfosFromTrades(this.formRecord.trades);
  }

  save() {
    this.formLoading = true;
    this.markForCheck();
    this.formRecord.trades.forEach(trade => {
      DateFormatPipe.checkDate(trade, 'businessDate');
      DateFormatPipe.checkDate(trade, 'clearingDate');
      DateFormatPipe.checkDate(trade, 'executionDate');
      trade.account = new Account();
      trade.account.name = trade['account.name'];
      trade.account.type = trade['account.type'];
      trade.account.reference1 = trade['account.reference1'];
      trade.account.reference2 = trade['account.reference2'];
      trade.account.reference3 = trade['account.reference3'];
    });
    if (!this.formRecord.file) {
      this.clearingFlexService.tradesRequest(this.formRecord).subscribe(validation => {
        if (validation.success) {
          this.closeDialog();
          this.infoMessage(validation.message);
        } else {
          this.ccFormDialog.ccForm.errorMessage(validation.message);
        }
        setTimeout(() => this.formLoading = false);
      });
      return;
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(this.formRecord.file);
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1];
        this.formRecord.fileName = this.formRecord.file.name;
        this.formRecord.base64FileContent = base64String;
        this.ccFormDialog.reloadFieldValueMap();
        this.clearingFlexService.tradesRequest(this.formRecord).subscribe(validation => {
          if (validation.success) {
            this.closeDialog();
            this.infoMessage(validation.message)
          } else {
            this.ccFormDialog.ccForm.errorMessage(validation.message);
          }
          setTimeout(() => this.formLoading = false);
        });
      };

    }
  }

  showDialog(): void {
    if (this.ccFormDialog)
      this.ccFormDialog.reloadFieldValueMap();
    this.formRecord = new FlexTradesRequest();
    if (this.dataLoadingService.owners.length > 0) {
      this.formRecord.ownerCode = this.dataLoadingService.owners[0].id;
    }
    if (this.dataLoadingService.tradeFlexTemplates.length > 0) {
      this.formRecord.mapping = this.dataLoadingService.tradeFlexTemplates[0].id;
    }

    this.dialogFormVisible = true;
    this.dialogFormVisibleChange.emit(true);
    this.refreshInfosFromTrades(this.formRecord.trades);
    this.markForCheck();
  }

}
